import { useEffect, useRef, useState } from 'react';

/**
 * Drag-to-rotate with directional touch detection.
 *
 * On touch devices we don't want to be a scroll trap: if the user's gesture
 * is primarily vertical (they want to scroll the page), we stay out of the way
 * and let the browser scroll natively. Only horizontal-dominant gestures
 * rotate the globe.
 *
 * This is implemented with `touch-action: pan-y` on the canvas (CSS) which
 * tells the browser to handle vertical panning, plus an axis-lock inside
 * pointermove that ignores predominantly-vertical movement for the first
 * few pixels of a touch gesture.
 */
const AXIS_LOCK_PX = 8; // px of movement before we decide the axis
const AXIS_LOCK_RATIO = 1.2; // |dx| must exceed |dy| by this factor for horizontal

export function useGlobeInteraction(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  setTheta: React.Dispatch<React.SetStateAction<number>>
): { dragging: boolean; idle: boolean } {
  const [dragging, setDragging] = useState(false);
  const [idle, setIdle] = useState(true);
  const draggingRef = useRef(false);
  const lastXRef = useRef(0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const axisLockedRef = useRef<'none' | 'horizontal' | 'vertical'>('none');
  const pointerTypeRef = useRef<string>('mouse');
  const velRef = useRef(0);
  const idleTimerRef = useRef<number | null>(null);
  const inertiaRafRef = useRef<number | null>(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const resetIdle = () => {
      setIdle(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => setIdle(true), 3000);
    };

    const onDown = (e: PointerEvent) => {
      draggingRef.current = true;
      pointerTypeRef.current = e.pointerType;
      lastXRef.current = e.clientX;
      startXRef.current = e.clientX;
      startYRef.current = e.clientY;
      axisLockedRef.current = e.pointerType === 'mouse' ? 'horizontal' : 'none';
      velRef.current = 0;
      if (inertiaRafRef.current) cancelAnimationFrame(inertiaRafRef.current);
      // Only capture pointer for mouse immediately. For touch, capture after
      // we've confirmed horizontal axis so vertical swipes stay with the page.
      if (e.pointerType === 'mouse') {
        el.setPointerCapture(e.pointerId);
        setDragging(true);
      }
    };

    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;

      // Resolve axis lock on touch/pen pointers.
      if (axisLockedRef.current === 'none') {
        const totalDx = Math.abs(e.clientX - startXRef.current);
        const totalDy = Math.abs(e.clientY - startYRef.current);
        if (totalDx < AXIS_LOCK_PX && totalDy < AXIS_LOCK_PX) return;
        if (totalDx > totalDy * AXIS_LOCK_RATIO) {
          axisLockedRef.current = 'horizontal';
          try {
            el.setPointerCapture(e.pointerId);
          } catch {
            /* ignore */
          }
          setDragging(true);
          lastXRef.current = e.clientX;
        } else {
          axisLockedRef.current = 'vertical';
          draggingRef.current = false;
          return;
        }
      }

      if (axisLockedRef.current !== 'horizontal') return;

      const dx = e.clientX - lastXRef.current;
      lastXRef.current = e.clientX;
      velRef.current = dx * 0.01;
      setTheta(t => t + dx * 0.01);
    };

    const onUp = (e: PointerEvent) => {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const wasDragging = draggingRef.current && axisLockedRef.current === 'horizontal';
      draggingRef.current = false;
      axisLockedRef.current = 'none';
      setDragging(false);
      if (!wasDragging) return;
      resetIdle();
      const decay = () => {
        if (Math.abs(velRef.current) < 0.0005) return;
        setTheta(t => t + velRef.current);
        velRef.current *= 0.95;
        inertiaRafRef.current = requestAnimationFrame(decay);
      };
      inertiaRafRef.current = requestAnimationFrame(decay);
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);

    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (inertiaRafRef.current) cancelAnimationFrame(inertiaRafRef.current);
    };
  }, [canvasRef, setTheta]);

  return { dragging, idle };
}
